import { useTranslation } from 'react-i18next'
import {
    createColumns,
    createDateFormatter,
} from '@/components/ui/data-table-customized'
import type {
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { buildAttachmentExpandable } from '@/components/ui/data-table-customized'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getSampleContracts } from '../services'
import type { SampleContract } from '../types'
import SampleAttachment from '../components/SampleAttachment'
import { useModal } from '@/contexts/modal'
import EditIcons from '@/icons/EditIcons'

export const useSampleList = (
    options: { value: string; label: string }[] = []
) => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const handleEdit = (row: SampleContract) => {
        setModal('ContractModals/SampleContractModal/index', {
            sampleContract: {
                ...row,
                attachments: row.attachments?.filter(
                    (att: any) => !att?.attachment_closed
                ),
            },
            mode: 'update',
        })
    }

    const { params, queryParams, setParam, setParams, searchInputProps } =
        useUrlSearchParams({
            defaults: { sort_by: 'updated_at', page_size: 10 },
        })

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACTS_SAMPLES, queryParams],
            queryFn: () => getSampleContracts(queryParams),
            placeholderData: true,
        },
        { showLoading: false }
    )

    const contracts = data?.results || []
    const pagination = data?.pagination

    const columns: DataTableColumn<SampleContract>[] =
        createColumns<SampleContract>([
            {
                key: 'contract_number_id',
                title: 'Contract ID',
                width: 220,
                cellClassName: 'text-primary font-medium',
            },
            {
                key: 'contract_key',
                title: t('contract_key'),
                width: 200,
            },
            {
                key: 'attachments' as any,
                title: t('attachment'),
                width: 300,
                sortable: false,
                render: (_, row) => {
                    const attachments = row.attachments || []
                    if (attachments.length === 0) return '-'
                    const first =
                        attachments.find(
                            (att: any) => !att?.attachment_closed
                        ) || attachments[0]
                    return (
                        <div className="flex items-center gap-1.5 w-full">
                            <div className="flex-1 min-w-0">
                                <SampleAttachment attachment={first} />
                            </div>
                            {attachments.length >= 2 && (
                                <span className="text-[10px] text-gray-400 flex-shrink-0">
                                    +{attachments.length - 1}
                                </span>
                            )}
                        </div>
                    )
                },
            },
            {
                key: 'product_group_name',
                title: t('product_group'),
                width: 250,
                render: (value) => {
                    if (!value) return '-'
                    return (
                        options?.find((item) => item.value === value)?.label ||
                        (value as string)
                    )
                },
            },
            {
                key: 'contract_note',
                title: t('contract_note'),
                width: 250,
                cellClassName: 'text-gray-600 truncate',
            },
            {
                key: 'employee_name',
                title: t('employee'),
                width: 200,
            },
            {
                key: 'updated_at',
                title: t('lasted_update'),
                width: 180,
                cellClassName: 'text-right italic text-gray-400',
                formatter: createDateFormatter({
                    format: 'DD/MM/YYYY HH:mm',
                }),
            },
        ])

    const actions: DataTableAction<SampleContract>[] = [
        {
            key: 'edit',
            label: t('edit'),
            icon: <EditIcons className="w-4 h-4" />,
            onClick: (row) => handleEdit(row),
        },
    ]

    const expandable = buildAttachmentExpandable<SampleContract>({
        getAttachments: (row) => row.attachments || [],
        t,
        openFilePreview,
        downloadFileWithPresigned,
    })

    const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') =>
        setParams({ sort_by: sortBy, sort_order: sortOrder })
    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (pageSize: number) =>
        setParams({ page_size: pageSize, page: 1 })

    return {
        contracts,
        columns,
        actions,
        expandable,
        pagination,
        params,
        searchInputProps,
        renderState,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
    }
}
